/**
 * Plan catalog + contact usage (V46). The catalog lives on the server
 * (server/src/config/plans.ts) and is served by GET /api/billing/plans; the
 * client never writes a price or a contact cap of its own.
 */
import { apiClient } from './apiClient';

export type PlanFamily = 'free' | 'pro' | 'scale' | 'custom';

export interface CatalogTier {
  id: string;
  family: PlanFamily;
  displayName: string;
  priceUsd: number | null;
  contactCap: number | null;
  channels: 'email_plus_one' | 'all';
  maxActiveFlows: number | null;
  inboxSeatsIncluded: number;
  selfServe: boolean;
}

export interface CatalogAddOn {
  id: string;
  displayName: string;
  priceUsd: number;
  unit: 'seat' | 'month';
  available: boolean;
}

export interface PlanCatalog {
  tiers: CatalogTier[];
  addOns: CatalogAddOn[];
  whatsappPassthroughMarkup: number;
  /**
   * Free-trial length on paid plans. The server catalog does not send it
   * today, so no page may promise a trial; copy that mentions one renders
   * only when this is a positive number.
   */
  trialDays?: number | null;
  overage: { warnAt: number; pauseAt: number };
}

export interface TierSummary {
  id: string;
  displayName: string;
  priceUsd: number | null;
  contactCap: number | null;
}

export type ContactCapState = 'ok' | 'warning' | 'over';

export interface ContactUsage {
  count: number;
  cap: number;
  pctUsed: number;
  state: ContactCapState;
  countedAt: string;
  tier: TierSummary;
  nextTier: TierSummary | null;
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The fields PlanLadder maps. A body without them (an empty workspace's stub,
 * a proxy page, an API that predates V46) is "unavailable", not a catalog:
 * V102 — `data: []` passed the old truthiness check and took
 * /settings/billing down on `catalog.tiers.map`.
 */
function isPlanCatalog(value: unknown): value is PlanCatalog {
  return (
    isObject(value)
    && Array.isArray(value.tiers) && value.tiers.every(isObject)
    && Array.isArray(value.addOns) && value.addOns.every(isObject)
  );
}

export async function fetchPlanCatalog(): Promise<PlanCatalog> {
  const res = await apiClient.get<Envelope<unknown>>('/billing/plans');
  if (!isPlanCatalog(res?.data)) throw new Error('Plan catalog unavailable');
  return res.data;
}

const CAP_STATES: readonly string[] = ['ok', 'warning', 'over'] satisfies readonly ContactCapState[];

/**
 * The fields the app-shell banner renders. A body without them (an API that
 * predates V46, a proxy page, a stub) is "unavailable", not a usage: the
 * banner reads anything but 'ok' as a warning and formats `cap`, so a bare
 * `{}` used to take the whole shell down.
 */
function isContactUsage(value: unknown): value is ContactUsage {
  if (typeof value !== 'object' || value === null) return false;
  const usage = value as Record<string, unknown>;
  return (
    typeof usage.state === 'string' && CAP_STATES.includes(usage.state)
    && Number.isFinite(usage.count) && Number.isFinite(usage.cap) && Number.isFinite(usage.pctUsed)
  );
}

export async function fetchContactUsage(): Promise<ContactUsage> {
  const res = await apiClient.get<Envelope<unknown>>('/billing/contacts');
  if (!isContactUsage(res?.data)) throw new Error('Contact usage unavailable');
  return res.data;
}

/** Start a Cardcom checkout for a catalog tier; resolves to the hosted payment URL. */
export async function initiateTierCheckout(tierId: string): Promise<string> {
  const res = await apiClient.post<Envelope<{ paymentUrl?: string }> & { paymentUrl?: string }>('/billing/initiate', {
    tierId,
    customerName: 'Account Holder', // Cardcom's hosted form collects the real name
    customerEmail: 'billing@buzzbitx.com', // overridden by Cardcom's hosted form
  });
  const url = res?.data?.paymentUrl ?? res?.paymentUrl;
  if (!url) throw new Error('No payment page was returned');
  return url;
}

/** Send a Shopify-billed merchant to Shopify's hosted plan page (Managed Pricing). */
export async function openShopifyPlanPage(tierId: string): Promise<string | null> {
  const res = await apiClient.post<Envelope<{ confirmationUrl?: string }>>('/integrations/shopify/billing/subscribe', {
    plan: tierId,
  });
  return res?.data?.confirmationUrl ?? null;
}
